
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ShoppingCart, Plus, Minus, Trash2, X, Zap } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Link, useNavigate } from "react-router-dom";
import { useVariantImages } from "@/hooks/useVariantImages";
import { useExpressCheckout } from "@/hooks/useExpressCheckout";
import { useAuth } from "@/context/AuthContext";

// Resolve and render the best image for a cart item
const CartItemImage = ({ item }: { item: any }) => {
  const { setCartItemThumbnail } = useCart();
  const variantInfo = item?.variantInfo;
  const hasDirect = Array.isArray(item?.image_urls) && item.image_urls.length > 0;

  const { imageUrls: variantImageUrls } = useVariantImages(
    item?.id,
    variantInfo?.size,
    variantInfo?.pot_type,
    variantInfo?.pot_color
  );

  const toAbsoluteHttps = (url: string): string => {
    if (!url) return '';
    let u = url.trim();
    if (u.startsWith('//')) u = `https:${u}`;
    if (u.startsWith('http://')) u = u.replace('http://', 'https://');
    if (u.startsWith('/')) u = `${window.location.origin}${u}`;
    return u;
  };

  const variantFirst = (variantImageUrls && variantImageUrls[0]) || undefined;
  const candidate =
    variantFirst ||
    item?.thumbnail?.url ||
    (hasDirect ? item.image_urls[0] : undefined) ||
    '/placeholder.svg';

  const src = toAbsoluteHttps(candidate);
  const alt = item?.thumbnail?.alt || item?.name || 'Product image';

  useEffect(() => {
    const best = variantFirst ? toAbsoluteHttps(variantFirst) : undefined;
    if (best && (!item?.thumbnail || item.thumbnail.url !== best)) {
      setCartItemThumbnail(item.cartItemId, { url: best, alt: alt, width: 96, height: 96 });
    }
  }, [variantFirst, item?.cartItemId]);

  return (
    <img
      src={src}
      alt={alt}
      className="w-16 h-16 object-cover rounded-md"
      loading="lazy"
      onError={(e) => {
        const el = e.currentTarget as HTMLImageElement;
        el.onerror = null;
        el.src = toAbsoluteHttps('/placeholder.svg');
      }}
      width={item?.thumbnail?.width || 64}
      height={item?.thumbnail?.height || 64}
    />
  );
};

export const CartDrawer = () => {
  const { 
    cartItems, 
    cartCount, 
    cartTotal, 
    updateQuantity, 
    removeFromCart,
    isCartDrawerOpen,
    closeCartDrawer,
    openCartDrawer
  } = useCart();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleRazorpayExpress, handleShiprocketCheckout, isLoading } = useExpressCheckout();

  const handleExpressCheckout = (type: 'razorpay' | 'shiprocket', event: React.MouseEvent) => {
    closeCartDrawer();
    if (type === 'razorpay') {
      handleRazorpayExpress();
    } else {
      handleShiprocketCheckout(undefined, event.nativeEvent);
    }
  };

  return (
    <Sheet open={isCartDrawerOpen} onOpenChange={(open) => open ? openCartDrawer() : closeCartDrawer()}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Shopping Cart ({cartCount})</SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={closeCartDrawer}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>
        
        <div className="flex flex-col h-full">
          {cartItems.length === 0 ? (
            <>
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
                <div className="w-32 h-32 mb-6 rounded-full bg-muted/30 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full bg-muted/50"></div>
                </div>
                <p className="text-muted-foreground text-base">Your shopping cart is empty</p>
              </div>
              <div className="pb-4">
                <Button className="w-full" size="lg" onClick={closeCartDrawer} asChild>
                  <Link to="/shop">Keep Shopping</Link>
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto py-4">
                <div className="space-y-6">
                  {cartItems.map((item) => (
                    <div key={item.cartItemId} className="flex gap-4">
                      <CartItemImage item={item} />
                      <div className="flex-1 space-y-1">
                        <h3 className="font-medium text-sm">{item.name}</h3>
                        {item.variantInfo ? (
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">
                              Pot: {item.variantInfo.pot_type} • Size: {item.variantInfo.size} • Color: {item.variantInfo.pot_color}
                            </p>
                          </div>
                        ) : (
                          <>
                            {item.selectedPotColor && (
                              <p className="text-xs text-muted-foreground">
                                Pot Color: {item.selectedPotColor.name}
                              </p>
                            )}
                            {item.selectedSize && (
                              <p className="text-xs text-muted-foreground">
                                Size: {item.selectedSize}
                              </p>
                            )}
                          </>
                        )}
                        <p className="text-sm font-medium">₹{item.price}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 text-center text-sm">{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => removeFromCart(item.cartItemId)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <Separator className="my-4" />
              
              <div className="space-y-4 pb-4">
                {/* Free shipping threshold message */}
                {cartTotal < 999 && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-center">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      Add ₹{(999 - cartTotal).toFixed(0)} more for <strong>FREE shipping</strong>
                    </p>
                  </div>
                )}
                {cartTotal >= 999 && (
                  <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3 text-center">
                    <p className="text-sm text-green-800 dark:text-green-200">
                      🎉 You've unlocked <strong>FREE shipping!</strong>
                    </p>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="font-medium">Subtotal</span>
                  <span className="font-bold text-lg">₹{cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className={cartTotal >= 999 ? 'text-green-600 dark:text-green-400' : ''}>
                    {cartTotal >= 999 ? 'FREE' : '₹70'}
                  </span>
                </div>
                
                {/* Express Checkout Options */}
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground text-center">Express Checkout</p>
                  <Button
                    onClick={(e) => handleExpressCheckout('razorpay', e)}
                    disabled={isLoading}
                    className="w-full bg-[#072654] hover:bg-[#0a3a7d] text-white"
                    size="lg"
                  >
                    <Zap className="mr-1.5 h-4 w-4" />
                    {isLoading ? 'Loading...' : 'Razorpay Express'}
                  </Button>
                </div>
                
                <div className="flex items-center gap-2">
                  <Separator className="flex-1" />
                  <span className="text-xs text-muted-foreground">or</span>
                  <Separator className="flex-1" />
                </div>
                
                <div className="space-y-2">
                  <Button asChild className="w-full" size="lg">
                    <Link to="/cart" onClick={closeCartDrawer}>View Cart</Link>
                  </Button>
                <Button asChild variant="outline" className="w-full" size="lg">
                    <Link to={user ? "/checkout" : "/auth"} onClick={closeCartDrawer}>Standard Checkout</Link>
                  </Button>
                  <Button variant="outline" className="w-full" size="lg" onClick={closeCartDrawer} asChild>
                    <Link to="/shop">Keep Shopping</Link>
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
